import { supabase } from "@/integrations/supabase/client";

/**
 * The company facts every post-auth gate needs: is onboarding done, which
 * logistics role, which dashboard mode, and is this a QA tenant.
 *
 * Lives here rather than in HomeGate because RequireLogisticsRole (wired at the
 * router level in App.tsx) needs it too, and HomeGate is lazy-loaded — importing
 * it eagerly would pull the whole dash-v2 home tree into the main bundle and
 * blow the hand-keyed chunk caps in scripts/chunk-baseline.json.
 *
 * HomeGate re-exports this so existing importers keep working.
 */
export interface GateState {
  company: {
    id: string;
    onboarding_complete: boolean;
    logistics_role: string | null;
    dashboard_mode: string | null;
    /** QA / overnight-suite tenants. Exempt from the mandatory role gate the
     *  same way they're exempt from onboarding — see RequireLogisticsRole. */
    is_test_tenant: boolean;
  } | null;
}

/** Shared react-query key. Every consumer must use this so the gates read one
 *  cached row instead of racing separate fetches with different shapes. */
export function gateStateQueryKey(userId: string | undefined) {
  return ["dash-v2", "home-gate", userId] as const;
}

export async function fetchGateState(userId: string): Promise<GateState> {
  // Owner first, then team-member fallback. Mirrors DispatcherHomeGate.
  let { data } = await supabase
    .from("companies")
    .select("id, onboarding_complete, settings, logistics_role, is_test_tenant")
    .eq("owner_user_id", userId)
    .maybeSingle();

  if (!data) {
    const { data: roleRow } = await supabase
      .from("company_roles")
      .select("company_id")
      .eq("user_id", userId)
      .not("accepted_at", "is", null)
      .order("accepted_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (roleRow?.company_id) {
      const { data: memberCompany } = await supabase
        .from("companies")
        .select("id, onboarding_complete, settings, logistics_role, is_test_tenant")
        .eq("id", roleRow.company_id)
        .maybeSingle();
      if (memberCompany) data = memberCompany;
    }
  }

  if (!data) return { company: null };

  const company = data as {
    id: string;
    onboarding_complete: boolean | null;
    settings: Record<string, unknown> | null;
    logistics_role: string | null;
    is_test_tenant: boolean | null;
  };
  // onboarding_complete is the canonical column. Legacy rows may have only
  // settings.onboarding_completed_at — treat either as "done". Test-tenant
  // accounts (is_test_tenant=true, used for QA / overnight suites) are
  // exempt from the onboarding wizard entirely so QA can reach the real
  // dashboard surface (overnight QA TOP ISSUE 2 — all 3 QA roles were
  // trapped on /dash/onboarding with a no-op "Skip for now"). This changes
  // nothing for real customers (is_test_tenant defaults to false).
  const isOnboarded =
    !!company.is_test_tenant ||
    !!company.onboarding_complete ||
    !!company.settings?.onboarding_completed_at;

  const dashboardMode = typeof company.settings?.dashboard_mode === "string"
    ? (company.settings.dashboard_mode as string)
    : null;

  return {
    company: {
      id: company.id,
      onboarding_complete: isOnboarded,
      logistics_role: company.logistics_role ?? null,
      dashboard_mode: dashboardMode,
      is_test_tenant: !!company.is_test_tenant,
    },
  };
}
